import type { ComponentChildren } from 'preact';
import { cn } from '@/lib/cn';

export type CardVariant = 'default' | 'inset' | 'outlined' | 'floating';
export type CardPadding = 'none' | 'sm' | 'md' | 'lg';
export type CardTone = 'default' | 'soft';
export type CardElevation = 'flat' | 'raised';
export type CardOverflow = 'hidden' | 'visible';
export type CardSectionTone = 'default' | 'muted';
export type CardSectionDivider = 'none' | 'top' | 'bottom';

export interface CardProps {
  as?: 'section' | 'article' | 'div' | 'aside';
  variant?: CardVariant;
  padding?: CardPadding;
  tone?: CardTone;
  elevation?: CardElevation;
  title?: ComponentChildren;
  subtitle?: ComponentChildren;
  actions?: ComponentChildren;
  header?: ComponentChildren;
  footer?: ComponentChildren;
  children?: ComponentChildren;
  className?: string;
  overflow?: CardOverflow;
  id?: string;
  onClick?: (event: MouseEvent) => void;
}

export interface CardSectionProps {
  children?: ComponentChildren;
  className?: string;
  tone?: CardSectionTone;
  divider?: CardSectionDivider;
  padded?: boolean;
}

const VARIANT: Record<CardVariant, string> = {
  default: 'bg-surface border-border-subtle dark:border-border-subtle/80',
  inset: 'bg-surface-2 border-border-subtle dark:bg-surface-2/70',
  outlined: 'bg-transparent border-border-subtle dark:border-border-subtle/60',
  floating: 'bg-surface border-border-subtle shadow-sm dark:shadow-md dark:border-border-subtle/80',
};

const BODY_PADDING: Record<CardPadding, string> = {
  none: 'p-0',
  sm: 'px-4 py-3',
  md: 'px-5 py-4',
  lg: 'px-6 py-5',
};

// Header y footer comparten escala del body para que el padding eje X
// quede alineado. `none` cae a `md` porque un Card padding="none" suele
// querer body sin padding (tablas, listas) pero el header sigue
// necesitando aire.
const FRAME_PADDING: Record<CardPadding, string> = {
  none: 'px-5 py-4',
  sm: 'px-4 py-3',
  md: 'px-5 py-4',
  lg: 'px-6 py-5',
};

const TONE: Record<CardTone, string> = {
  default: '',
  soft: 'opacity-95 backdrop-blur-[2px]',
};

const ELEVATION: Record<CardElevation, string> = {
  flat: '',
  raised: 'shadow-sm',
};

const OVERFLOW: Record<CardOverflow, string> = {
  hidden: 'overflow-hidden',
  visible: 'overflow-visible',
};

const SECTION_TONE: Record<CardSectionTone, string> = {
  default: 'bg-transparent',
  muted: 'bg-surface-2 shadow-inner',
};

const SECTION_DIVIDER: Record<CardSectionDivider, string> = {
  none: '',
  top: 'border-t border-border-subtle',
  bottom: 'border-b border-border-subtle',
};

export function Card({
  as: Tag = 'section',
  variant = 'default',
  padding = 'md',
  tone = 'default',
  elevation = variant === 'floating' ? 'raised' : 'flat',
  title,
  subtitle,
  actions,
  header,
  footer,
  children,
  className,
  overflow = 'hidden',
  id,
  onClick,
}: CardProps) {
  const hasHeader = header || title || subtitle || actions;
  return (
    <Tag
      id={id}
      onClick={onClick}
      className={cn(
        // Refresh 2026-05-20: transition más suave (ease-smooth en vez del
        // default ease-snappy); hover de cards clickables gana
        // `hover:shadow-md` para sensación más "lift" en lugar de la sombra
        // mínima `shadow-sm`.  Activo conserva el micro-scale 0.995.
        'flex min-w-0 flex-col rounded-lg border text-fg',
        'transition-[background-color,border-color,box-shadow,transform] duration-med ease-smooth',
        VARIANT[variant],
        TONE[tone],
        ELEVATION[elevation],
        OVERFLOW[overflow],
        onClick && 'cursor-pointer hover:border-border hover:shadow-md active:scale-[0.995] active:transition-none',
        className,
      )}
    >
      {hasHeader ? (
        <header
          className={cn(
            'flex min-w-0 flex-col gap-3 border-b border-border-subtle sm:flex-row sm:items-start sm:justify-between',
            FRAME_PADDING[padding],
          )}
        >
          {header ? (
            header
          ) : (
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              {title ? <h3 className="text-sm font-semibold tracking-normal leading-snug text-fg-strong">{title}</h3> : null}
              {subtitle ? <p className="text-xs leading-[1.4] text-fg-muted mt-0.5">{subtitle}</p> : null}
            </div>
          )}
          {actions ? <div className="flex min-w-0 shrink-0 flex-wrap items-center gap-2">{actions}</div> : null}
        </header>
      ) : null}
      <div className={cn('min-w-0 flex-1', BODY_PADDING[padding])}>{children}</div>
      {footer ? (
        <footer
          className={cn(
            'flex flex-wrap items-center justify-end gap-2 border-t border-border-subtle',
            FRAME_PADDING[padding],
          )}
        >
          {footer}
        </footer>
      ) : null}
    </Tag>
  );
}

export function CardSection({
  children,
  className,
  tone = 'default',
  divider = 'none',
  padded = true,
}: CardSectionProps) {
  return (
    <div
      className={cn(
        'min-w-0',
        SECTION_TONE[tone],
        SECTION_DIVIDER[divider],
        padded && 'px-4 py-3',
        className,
      )}
    >
      {children}
    </div>
  );
}
